// File: src/components/dashboard/ProgressVisualizationTab.js
// This component is updated with custom date ranges and more detailed charts.
import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// =================================================================================
// MOCK DATA GENERATION (Expanded with more metrics)
// =================================================================================
const generateMockData = () => {
    const data = { weight: [], performance: {}, nutrition: [] };
    let currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - 3); // Start 3 months ago

    for (let i = 0; i < 90; i++) {
        const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD

        // Body Data (every 3 days)
        if (i % 3 === 0) {
            data.weight.push({ 
                date: dateStr, 
                weight: 80 - (i / 10) + Math.random() * 1.5,
                bodyFat: 22 - (i / 15) + Math.random() * 1,
            });
        }

        // Performance Data (for key lifts, twice a week)
        if (currentDate.getDay() === 1 || currentDate.getDay() === 4) { // Mondays & Thursdays
            const exercises = ['Bench Press', 'Squat', 'Deadlift'];
            exercises.forEach((ex, index) => {
                if (!data.performance[ex]) data.performance[ex] = [];
                const baseVolume = 1000 + (index * 500);
                const volume = (baseVolume + (i * (15 + index * 5)) + Math.random() * 200);
                data.performance[ex].push({ date: dateStr, volume: Math.round(volume) });
            });
        }
        
        // Nutrition Data (daily)
        data.nutrition.push({
            date: dateStr,
            calories: 2200 + (Math.random() * 400 - 200),
            protein: 150 + (Math.random() * 20 - 10),
            carbs: 250 + (Math.random() * 30 - 15),
            fat: 70 + (Math.random() * 15 - 7.5),
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return data;
};
const mockProgressData = generateMockData();
const availablePerformanceMetrics = Object.keys(mockProgressData.performance);

// =================================================================================
// HELPER COMPONENTS
// =================================================================================
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (<div className="bg-gray-800 p-3 border border-gray-600 rounded-lg shadow-lg"><p className="label text-lime">{`${label}`}</p>{payload.map((p, i) => (<p key={i} style={{ color: p.color }}>{`${p.name}: ${p.value.toFixed(1)}`}</p>))}</div>);
    } return null;
};

// =================================================================================
// MAIN COMPONENT
// =================================================================================
function ProgressVisualizationTab({ user, userSettings }) {
  const [progressSubTab, setProgressSubTab] = useState('body');
  const [timeRange, setTimeRange] = useState({ type: 'preset', days: 30 }); // preset or custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedPerformanceExercise, setSelectedPerformanceExercise] = useState(availablePerformanceMetrics[0]);

  const { unitSystem } = userSettings;

  const SubTabButton = ({ tabName, children }) => (<button onClick={() => setProgressSubTab(tabName)} className={`px-4 py-2 font-semibold rounded-md transition-colors duration-300 ${progressSubTab === tabName ? 'bg-magenta text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>{children}</button>);

  const handleTimeRangePreset = (days) => {
    setTimeRange({ type: 'preset', days });
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
        setTimeRange({ type: 'custom' });
    }
  };

  const filteredProgressData = useMemo(() => {
    let startDate, endDate;

    if (timeRange.type === 'custom' && customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        // Add one day to end date to make it inclusive
        endDate.setDate(endDate.getDate() + 1);
    } else {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(endDate.getDate() - timeRange.days);
    }

    const filterByDate = (data) => data.filter(item => { 
        const itemDate = new Date(item.date); 
        return itemDate >= startDate && itemDate <= endDate; 
    });

    const performanceData = mockProgressData.performance[selectedPerformanceExercise] || [];

    return { 
        weight: filterByDate(mockProgressData.weight), 
        performance: filterByDate(performanceData),
        nutrition: filterByDate(mockProgressData.nutrition) 
    };
  }, [timeRange, customStartDate, customEndDate, selectedPerformanceExercise]);

  return (
    <>
      <h3 className="text-3xl font-bold mb-6 text-lime">Visualize Your Progress</h3>
      
      {/* Filters Row */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div className="flex items-center space-x-2">
          <SubTabButton tabName="body">Body</SubTabButton>
          <SubTabButton tabName="performance">Performance</SubTabButton>
          <SubTabButton tabName="nutrition">Nutrition</SubTabButton>
        </div>
        <div className="flex items-center flex-wrap gap-4">
            <div className="flex items-center space-x-2">
                <input type="date" value={customStartDate} onChange={e => {setCustomStartDate(e.target.value); handleCustomDateChange();}} className="bg-gray-700 text-white rounded-md p-1 text-sm border-gray-600 focus:ring-magenta"/>
                <span className="text-gray-400">to</span>
                <input type="date" value={customEndDate} onChange={e => {setCustomEndDate(e.target.value); handleCustomDateChange();}} className="bg-gray-700 text-white rounded-md p-1 text-sm border-gray-600 focus:ring-magenta"/>
            </div>
            <div className="flex items-center space-x-2">
                {[{ label: '1M', days: 30 }, { label: '3M', days: 90 }].map(({label, days}) => (<button key={days} onClick={() => handleTimeRangePreset(days)} className={`px-3 py-1 text-sm font-bold rounded-md ${timeRange.type === 'preset' && timeRange.days === days ? 'bg-magenta text-black' : 'bg-gray-700 hover:bg-gray-600'}`}>{label}</button>))}
            </div>
        </div>
      </div>

      {/* Charts Area */}
      <div className="min-h-[24rem]">
        {progressSubTab === 'body' && (
            <>
                <h4 className="text-xl font-bold mb-4 text-magenta">Weight & Body Fat %</h4>
                <ResponsiveContainer width="100%" height={384}>
                    <LineChart data={filteredProgressData.weight} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                        <XAxis dataKey="date" stroke="#A0AEC0" tick={{fontSize: 12}} />
                        <YAxis yAxisId="left" stroke="#C026D3" tick={{fontSize: 12}} domain={['dataMin - 2', 'dataMax + 2']}/>
                        <YAxis yAxisId="right" orientation="right" stroke="#AFFF00" tick={{fontSize: 12}} domain={['dataMin - 2', 'dataMax + 2']}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="weight" stroke="#C026D3" strokeWidth={2} name={`Weight (${unitSystem === 'metric' ? 'kg' : 'lbs'})`} />
                        <Line yAxisId="right" type="monotone" dataKey="bodyFat" stroke="#AFFF00" strokeWidth={2} name="Body Fat %" />
                    </LineChart>
                </ResponsiveContainer>
            </>
        )}
        {progressSubTab === 'performance' && (
             <>
                <div className="flex items-center mb-4 gap-4">
                    <h4 className="text-xl font-bold text-magenta">Performance: Total Volume</h4>
                    <select value={selectedPerformanceExercise} onChange={e => setSelectedPerformanceExercise(e.target.value)} className="bg-gray-700 text-white rounded-md p-1 text-sm border-gray-600 focus:ring-magenta">
                        {availablePerformanceMetrics.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                    </select>
                </div>
                <ResponsiveContainer width="100%" height={384}>
                    <LineChart data={filteredProgressData.performance} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                        <XAxis dataKey="date" stroke="#A0AEC0" tick={{fontSize: 12}} />
                        <YAxis stroke="#A0AEC0" tick={{fontSize: 12}}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="volume" stroke="#AFFF00" strokeWidth={2} name={`Volume (${unitSystem === 'metric' ? 'kg' : 'lbs'})`} />
                    </LineChart>
                </ResponsiveContainer>
            </>
        )}
        {progressSubTab === 'nutrition' && (
            <>
                <h4 className="text-xl font-bold mb-4 text-magenta">Daily Nutrition Intake</h4>
                 <ResponsiveContainer width="100%" height={384}>
                    <LineChart data={filteredProgressData.nutrition} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                        <XAxis dataKey="date" stroke="#A0AEC0" tick={{fontSize: 10}} interval={Math.max(1, Math.floor(filteredProgressData.nutrition.length / 10))}/>
                        <YAxis stroke="#A0AEC0" tick={{fontSize: 12}}/>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="calories" stroke="#C026D3" strokeWidth={2} name="Calories" />
                        <Line type="monotone" dataKey="protein" stroke="#AFFF00" strokeWidth={2} name="Protein (g)" />
                        <Line type="monotone" dataKey="carbs" stroke="#38BDF8" strokeWidth={2} name="Carbs (g)" />
                        <Line type="monotone" dataKey="fat" stroke="#FBBF24" strokeWidth={2} name="Fat (g)" />
                    </LineChart>
                </ResponsiveContainer>
            </>
        )}
      </div>
    </>
  );
}

export default ProgressVisualizationTab;

